import React from 'react'
import { useDispatch } from 'react-redux'
import { UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'
import { AsyncAction } from '@cbgms/base/redux/async-action'
import { Time } from '@carsys/enums/time'
import { GETPriceRulesResponse, GETPriceRulesSettingsResponse, IPriceRulesSettings } from '@cbgms/api/modules/price-rules'
import * as actions from '../../state/PriceMarkups/actions'
import { priceRulesCacheKey, priceRulesSettingsCacheKey } from '../../../../hooks/cache-keys'

export interface UsePriceRulesResult {
  priceRules: UseQueryResult<GETPriceRulesResponse['Data']>
  priceRulesSettings: UseQueryResult<IPriceRulesSettings>
  isEnabled: boolean
  productWiseDetails: GETPriceRulesResponse['Data']
  refetch: () => void
  refetchSettings: () => void
}

export const usePriceRules = (): UsePriceRulesResult => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  // Load price rules
  const priceRules = useQuery(
    [priceRulesCacheKey],
    async () => {
      const { Data: rules } = (await dispatch(AsyncAction<GETPriceRulesResponse>(actions.loadPriceRules()))) as any as GETPriceRulesResponse
      return rules
    },
    {
      staleTime: Time.Minute * 5
    }
  )

  // Load price rules settings
  const priceRulesSettings = useQuery(
    [priceRulesSettingsCacheKey],
    async () => {
      const { Data: settings } = (await dispatch(
        AsyncAction<GETPriceRulesSettingsResponse>(actions.loadPriceRulesSettings())
      )) as any as GETPriceRulesSettingsResponse
      return settings
    },
    {
      staleTime: Time.Minute * 5
    }
  )

  // Refetch functions
  const refetch = React.useCallback(() => {
    queryClient.invalidateQueries([priceRulesCacheKey])
  }, [queryClient])

  const refetchSettings = React.useCallback(() => {
    queryClient.invalidateQueries([priceRulesSettingsCacheKey])
  }, [queryClient])

  // Check if price rules are enabled and there are valid rules configured
  const isEnabled = React.useMemo(() => {
    const settingsEnabled = priceRulesSettings.data?.Enabled ?? false
    if (!settingsEnabled) {
      return false
    }

    // Check if there are any valid rules (either default rule or product-specific rules)
    const rules = priceRules.data ?? []
    const hasValidRules = rules.some(
      rule => rule.PriceRule.Value !== null && rule.PriceRule.Value !== undefined && rule.PriceRule.Value !== 0
    )

    return hasValidRules
  }, [priceRulesSettings.data?.Enabled, priceRules.data])

  return {
    priceRules,
    priceRulesSettings,
    isEnabled,
    productWiseDetails: priceRules.data ?? [],
    refetch,
    refetchSettings
  }
}
